import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { apiService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import './Memcached.css';

interface MemcachedData {
  memcached: {
    status: 'Healthy' | 'Unreachable';
    endpoint: string;
    error?: string;
    uptimeSeconds: number;
    uptimeFormatted: string;
    bytesUsed: number;
    bytesLimit: number;
    bytesFree: number;
    memoryPercent: number;
    getHits: number;
    getMisses: number;
    cmdGet: number;
    hitRatePercent: number;
    currItems: number;
    currConnections: number;
    evictions: number;
  };
  cpu: {
    instanceId: string;
    currentPercent: number;
    averagePercent: number;
    peakPercent: number;
    datapoints: Array<{
      timestamp: string;
      average: number;
      maximum: number;
    }>;
    error?: string;
  };
}

const Memcached: React.FC = () => {
  const { showError } = useNotification();
  const [data, setData] = useState<MemcachedData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [lastUpdated, setLastUpdated] = useState<string>('');

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getMemcachedStats();
      setData(res.data);
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (err: any) {
      console.error('Failed to load Memcached statistics:', err);
      showError(err.message || 'Failed to fetch Memcached statistics');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const formatMB = (bytes: number) => {
    if (!bytes || bytes <= 0) return '0.00 MB';
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const mem = data?.memcached;
  const cpu = data?.cpu;
  const isHealthy = mem?.status === 'Healthy';

  // Generate SVG Sparkline for CPU datapoints
  const renderCpuChart = () => {
    if (!cpu || !cpu.datapoints || cpu.datapoints.length === 0) {
      return (
        <div className="cpu-empty-chart">
          No CloudWatch CPU datapoints available in the last 6 hours.
        </div>
      );
    }

    const dps = cpu.datapoints;
    const maxVal = Math.max(...dps.map((d) => d.maximum), 2.0); // at least 2% ceiling for chart
    const width = 600;
    const height = 140;
    const padding = 20;

    const points = dps.map((d, idx) => {
      const x = padding + (idx / Math.max(dps.length - 1, 1)) * (width - 2 * padding);
      const y = height - padding - (d.average / maxVal) * (height - 2 * padding);
      return `${x},${y}`;
    });

    const maxPoints = dps.map((d, idx) => {
      const x = padding + (idx / Math.max(dps.length - 1, 1)) * (width - 2 * padding);
      const y = height - padding - (d.maximum / maxVal) * (height - 2 * padding);
      return `${x},${y}`;
    });

    const linePath = `M ${points.join(' L ')}`;
    const maxLinePath = `M ${maxPoints.join(' L ')}`;

    // Area fill under average line
    const firstPoint = points[0].split(',');
    const lastPoint = points[points.length - 1].split(',');
    const areaPath = `M ${points.join(' L ')} L ${lastPoint[0]},${height - padding} L ${firstPoint[0]},${height - padding} Z`;

    return (
      <div className="cpu-chart-container">
        <svg viewBox={`0 0 ${width} ${height}`} className="cpu-sparkline-svg">
          <defs>
            <linearGradient id="cpuGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#4f46e5" stopOpacity="0.0" />
            </linearGradient>
          </defs>

          {/* Grid lines */}
          <line x1={padding} y1={padding} x2={width - padding} y2={padding} stroke="#e2e8f0" strokeDasharray="3 3" />
          <line
            x1={padding}
            y1={height / 2}
            x2={width - padding}
            y2={height / 2}
            stroke="#e2e8f0"
            strokeDasharray="3 3"
          />
          <line
            x1={padding}
            y1={height - padding}
            x2={width - padding}
            y2={height - padding}
            stroke="#cbd5e1"
          />

          {/* Fill area */}
          <path d={areaPath} fill="url(#cpuGradient)" />

          {/* Max line (peak) */}
          <path d={maxLinePath} fill="none" stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="4 2" />

          {/* Average line */}
          <path d={linePath} fill="none" stroke="#4f46e5" strokeWidth="2.5" />

          {/* Points */}
          {dps.map((d, idx) => {
            const x = padding + (idx / Math.max(dps.length - 1, 1)) * (width - 2 * padding);
            const y = height - padding - (d.average / maxVal) * (height - 2 * padding);
            return (
              <circle
                key={idx}
                cx={x}
                cy={y}
                r="3"
                fill="#4f46e5"
                className="chart-dot"
              >
                <title>{`${new Date(d.timestamp).toLocaleTimeString()}: Avg ${d.average.toFixed(2)}%, Max ${d.maximum.toFixed(2)}%`}</title>
              </circle>
            );
          })}
        </svg>

        <div className="chart-legend">
          <div className="legend-item">
            <span className="legend-indicator avg-indicator"></span>
            <span>Average CPU (%)</span>
          </div>
          <div className="legend-item">
            <span className="legend-indicator peak-indicator"></span>
            <span>Peak CPU (%)</span>
          </div>
          <div className="legend-timeframe">
            Last 6 Hours (5-minute CloudWatch datapoints)
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="memcached-page">
        {/* Header Section */}
        <div className="memcached-header">
          <div className="header-info">
            <div className="title-row">
              <h2>Memcached Cluster</h2>
              <span className={`cluster-status-pill ${isHealthy ? 'status-healthy' : 'status-unreachable'}`}>
                <span className="pulse-dot"></span>
                {mem?.status || (loading ? 'Checking...' : 'Unknown')}
              </span>
            </div>
            <p className="endpoint-text">
              Endpoint: <code>{mem?.endpoint || 'ap-southeast-1.memcached.softlandsystems.com:11211'}</code>
            </p>
          </div>

          <div className="header-actions">
            {lastUpdated && <span className="last-updated-text">Updated at {lastUpdated}</span>}
            <button
              onClick={fetchStats}
              disabled={loading}
              className={`refresh-btn ${loading ? 'loading' : ''}`}
            >
              <svg
                className={`refresh-icon ${loading ? 'spin' : ''}`}
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
              </svg>
              {loading ? 'Refreshing...' : 'Refresh Stats'}
            </button>
          </div>
        </div>

        {mem?.error && (
          <div className="memcached-error-alert">
            <strong>Warning:</strong> {mem.error}
          </div>
        )}

        {/* Top KPI Cards */}
        <div className="kpi-grid">
          <div className="kpi-card">
            <span className="kpi-label">Uptime</span>
            <span className="kpi-value">{mem?.uptimeFormatted || '0s'}</span>
            <span className="kpi-subtext">Running without interruption</span>
          </div>

          <div className="kpi-card">
            <span className="kpi-label">Hit Ratio</span>
            <span className="kpi-value highlight-green">
              {mem ? `${mem.hitRatePercent.toFixed(1)}%` : '0%'}
            </span>
            <span className="kpi-subtext">Overall Cache Efficiency</span>
          </div>

          <div className="kpi-card">
            <span className="kpi-label">Total Cache Hits</span>
            <span className="kpi-value">{mem ? mem.getHits.toLocaleString() : '0'}</span>
            <span className="kpi-subtext">Served from RAM</span>
          </div>

          <div className="kpi-card">
            <span className="kpi-label">Total Cache Misses</span>
            <span className="kpi-value highlight-amber">
              {mem ? mem.getMisses.toLocaleString() : '0'}
            </span>
            <span className="kpi-subtext">Fell back to DynamoDB</span>
          </div>
        </div>

        {/* Memory Allocation Card */}
        <div className="content-card">
          <div className="card-header-row">
            <h3>Memory Allocation</h3>
            <span className="card-badge">
              {mem ? `${mem.memoryPercent.toFixed(2)}% Used` : '0%'}
            </span>
          </div>

          <div className="memory-progress-wrapper">
            <div className="memory-progress-bar">
              <div
                className="memory-progress-fill"
                style={{ width: `${Math.max(mem?.memoryPercent || 0, 1)}%` }}
              ></div>
            </div>
            <div className="memory-labels">
              <span>
                <strong>Used:</strong> {mem ? formatMB(mem.bytesUsed) : '0 MB'}
              </span>
              <span>
                <strong>Free:</strong> {mem ? formatMB(mem.bytesFree) : '0 MB'}
              </span>
              <span>
                <strong>Max Limit:</strong> {mem ? formatMB(mem.bytesLimit) : '0 MB'}
              </span>
            </div>
          </div>

          <div className="detail-metrics-row">
            <div className="detail-box">
              <span className="detail-number">{mem ? mem.currItems.toLocaleString() : '0'}</span>
              <span className="detail-title">Active Cached Items</span>
            </div>
            <div className="detail-box">
              <span className="detail-number">{mem ? mem.currConnections.toLocaleString() : '0'}</span>
              <span className="detail-title">Active Connections</span>
            </div>
            <div className="detail-box">
              <span className="detail-number">{mem ? mem.evictions.toLocaleString() : '0'}</span>
              <span className="detail-title">Evictions (Out of RAM)</span>
            </div>
            <div className="detail-box">
              <span className="detail-number">{mem ? mem.cmdGet.toLocaleString() : '0'}</span>
              <span className="detail-title">Total Read Commands</span>
            </div>
          </div>
        </div>

        {/* CPU Utilization (Last 6 Hours) Card */}
        <div className="content-card">
          <div className="card-header-row">
            <div>
              <h3>EC2 CPU Utilization (Last 6 Hours)</h3>
              {cpu?.instanceId && (
                <span className="instance-id-pill">Instance: {cpu.instanceId}</span>
              )}
            </div>

            <div className="cpu-summary-pills">
              <span className="cpu-pill">
                Current: <strong>{cpu ? `${cpu.currentPercent.toFixed(2)}%` : '0%'}</strong>
              </span>
              <span className="cpu-pill">
                6h Avg: <strong>{cpu ? `${cpu.averagePercent.toFixed(2)}%` : '0%'}</strong>
              </span>
              <span className="cpu-pill">
                6h Peak: <strong>{cpu ? `${cpu.peakPercent.toFixed(2)}%` : '0%'}</strong>
              </span>
            </div>
          </div>

          {cpu?.error && (
            <div className="cpu-error-message">
              <span>{cpu.error}</span>
            </div>
          )}

          {renderCpuChart()}
        </div>
      </div>
    </Layout>
  );
};

export default Memcached;
